/**
 * Smart Input Parser
 * Parses natural language, structured text, and JSON into travel document fields
 */

import { extractAirportCodes, isValidAirportCode } from '../../utils/airportCodes';
import { extractAirlineCode, extractFlightNumber, getCodeFromName, AIRLINES } from '../../utils/airlineCodes';
import { parseDate, parseTime, extractDates, extractTimes, combineDateTime } from '../../utils/dateParser';
import { getSchema } from '../../utils/fieldSchemas';

/**
 * Main parser class
 */
class SmartInputParser {
  constructor(documentType = 'flight', referenceDate = new Date()) {
    this.documentType = documentType;
    this.referenceDate = referenceDate;
    this.schema = getSchema(documentType);
  }

  /**
   * Parse input text and return extracted fields
   */
  parse(input) {
    if (!input || typeof input !== 'string') {
      return { success: false, data: {}, confidence: {}, errors: ['No input provided'] };
    }

    const trimmed = input.trim();

    // Detect input format
    const format = this.detectFormat(trimmed);

    let result;
    switch (format) {
      case 'json':
        result = this.parseJSON(trimmed);
        break;
      case 'key-value':
        result = this.parseKeyValue(trimmed);
        break;
      case 'tabular':
        result = this.parseTabular(trimmed);
        break;
      default:
        result = this.parseNatural(trimmed);
    }

    // Add format info
    result.format = format;

    return result;
  }

  /**
   * Detect the input format
   */
  detectFormat(input) {
    // Check for JSON
    if (input.startsWith('{') || input.startsWith('[')) {
      try {
        JSON.parse(input);
        return 'json';
      } catch {
        // Not valid JSON, continue
      }
    }

    // Check for key-value format (multiple lines with colons)
    const lines = input.split('\n').filter(l => l.trim());
    const kvLines = lines.filter(l => /^[a-z_\s]+:/i.test(l.trim()));
    if (kvLines.length >= 2 && kvLines.length >= lines.length * 0.6) {
      return 'key-value';
    }

    // Check for tabular format (tabs or pipes)
    if (input.includes('\t') || input.includes('|')) {
      return 'tabular';
    }

    return 'natural';
  }

  /**
   * Parse JSON input
   */
  parseJSON(input) {
    try {
      const data = JSON.parse(input);
      return {
        success: true,
        data: this.normalizeData(data),
        confidence: this.calculateConfidence(data),
        errors: []
      };
    } catch (e) {
      return {
        success: false,
        data: {},
        confidence: {},
        errors: [`Invalid JSON: ${e.message}`]
      };
    }
  }

  /**
   * Parse key-value format
   */
  parseKeyValue(input) {
    const data = {};
    const lines = input.split('\n');

    for (const line of lines) {
      const match = line.match(/^([a-z_\s]+):\s*(.+)$/i);
      if (match) {
        const key = this.normalizeFieldName(match[1]);
        const value = match[2].trim();
        if (key && value) {
          data[key] = value;
        }
      }
    }

    return {
      success: Object.keys(data).length > 0,
      data: this.normalizeData(data),
      confidence: this.calculateConfidence(data),
      errors: []
    };
  }

  /**
   * Parse tabular format (from copied tables)
   */
  parseTabular(input) {
    const data = {};
    const separator = input.includes('|') ? '|' : '\t';
    const cells = input.split(separator).map(c => c.trim()).filter(c => c);

    // Try to extract from cells
    const airports = [];
    const dates = [];
    const times = [];

    for (const cell of cells) {
      // Check for airport codes
      const airportCodes = extractAirportCodes(cell.toUpperCase());
      airports.push(...airportCodes);

      // Check for dates
      const parsedDate = parseDate(cell, this.referenceDate);
      if (parsedDate) dates.push(parsedDate);

      // Check for times
      const parsedTime = parseTime(cell);
      if (parsedTime) times.push(parsedTime);

      // Check for flight numbers
      const flightNum = extractFlightNumber(cell);
      if (flightNum && !data.flight_number) {
        data.flight_number = flightNum;
        const airlineCode = extractAirlineCode(cell);
        if (airlineCode) data.airline_code = airlineCode;
      }

      // Check for confirmation numbers (6-8 alphanumeric)
      const confMatch = cell.match(/^[A-Z0-9]{5,8}$/i);
      if (confMatch && !data.confirmation_number) {
        data.confirmation_number = cell.toUpperCase();
      }
    }

    // Assign airports
    if (airports.length >= 2) {
      data.departure_airport = airports[0];
      data.arrival_airport = airports[1];
    } else if (airports.length === 1) {
      data.departure_airport = airports[0];
    }

    // Assign dates
    if (dates.length >= 1) {
      data.departure_date = dates[0];
      if (dates.length >= 2) {
        data.arrival_date = dates[1];
      }
    }

    // Assign times
    if (times.length >= 1) {
      data.departure_time = times[0];
      if (times.length >= 2) {
        data.arrival_time = times[1];
      }
    }

    return {
      success: Object.keys(data).length > 0,
      data: this.normalizeData(data),
      confidence: this.calculateConfidence(data),
      errors: []
    };
  }

  /**
   * Parse natural language input
   */
  parseNatural(input) {
    const data = {};
    const upperInput = input.toUpperCase();
    const confidence = {};

    // Extract based on document type
    switch (this.documentType) {
      case 'flight':
      case 'commercial_flight':
        this.parseFlightNatural(input, upperInput, data, confidence);
        break;
      case 'hotel':
        this.parseHotelNatural(input, upperInput, data, confidence);
        break;
      case 'car_rental':
        this.parseCarRentalNatural(input, upperInput, data, confidence);
        break;
      case 'private_flight':
        this.parsePrivateFlightNatural(input, upperInput, data, confidence);
        break;
      default:
        this.parseFlightNatural(input, upperInput, data, confidence);
    }

    // Try to extract common fields regardless of type
    this.extractCommonFields(input, upperInput, data, confidence);

    return {
      success: Object.keys(data).length > 0,
      data: this.normalizeData(data),
      confidence,
      errors: []
    };
  }

  /**
   * Parse flight-specific fields from natural language
   * Supports multi-leg flights via:
   * - Explicit markers: "L1"/"L2", "Leg 1"/"Leg 2"
   * - Multiple arrow notations: "JFK → LAX", "LAX → SFO" on separate lines
   * - Semicolons: "JFK-LAX 10am; LAX-SFO 3pm"
   */
  parseFlightNatural(input, upperInput, data, confidence) {
    // Try to split into multiple legs
    const legTexts = this._splitIntoLegs(input);

    if (legTexts.length > 1) {
      // Multi-leg: parse each leg separately
      const legs = [];
      for (let i = 0; i < legTexts.length; i++) {
        const legData = {};
        const legConfidence = {};
        this._parseSingleFlightLeg(legTexts[i], legTexts[i].toUpperCase(), legData, legConfidence);
        if (Object.keys(legData).length > 0) {
          legData.leg_number = i + 1;
          legs.push(legData);
        }
      }
      if (legs.length > 1) {
        data.legs = legs;
        confidence.legs = 0.85;
        return;
      }
      // Fall through to single-leg if only 1 leg parsed successfully
      if (legs.length === 1) {
        Object.assign(data, legs[0]);
        delete data.leg_number;
      }
      return;
    }

    // Single-leg: parse normally
    this._parseSingleFlightLeg(input, upperInput, data, confidence);
  }

  /**
   * Split input text into separate leg segments
   */
  _splitIntoLegs(input) {
    // 1. Explicit leg markers: "L1:", "Leg 1:", "Leg1:", "L1 ", case insensitive
    const legMarkerPattern = /(?:^|\n)\s*(?:L(?:eg)?\s*(\d+)\s*[:\-]?\s*)/gi;
    const markers = [...input.matchAll(legMarkerPattern)];
    if (markers.length >= 2) {
      const segments = [];
      for (let i = 0; i < markers.length; i++) {
        const start = markers[i].index + markers[i][0].length;
        const end = i < markers.length - 1 ? markers[i + 1].index : input.length;
        const text = input.substring(start, end).trim();
        if (text) segments.push(text);
      }
      if (segments.length >= 2) return segments;
    }

    // 2. Semicolons with airport codes on each side
    if (input.includes(';')) {
      const parts = input.split(';').map(s => s.trim()).filter(s => s);
      // Each part should contain at least one airport code to be a valid leg
      const validParts = parts.filter(p => {
        const codes = extractAirportCodes(p.toUpperCase());
        return codes.length >= 1;
      });
      if (validParts.length >= 2) return validParts;
    }

    // 3. Multiple arrow notations on separate lines
    const lines = input.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.length >= 2) {
      const arrowLines = lines.filter(l =>
        /[A-Z]{3}\s*(?:->|→|-|to)\s*[A-Z]{3}/i.test(l)
      );
      if (arrowLines.length >= 2) return arrowLines;
    }

    // 4. Multiple lines each with 2+ airport codes
    if (lines.length >= 2) {
      const airportLines = lines.filter(l => {
        const codes = extractAirportCodes(l.toUpperCase());
        return codes.length >= 2;
      });
      if (airportLines.length >= 2) return airportLines;
    }

    // Single leg
    return [input];
  }

  /**
   * Parse a single flight leg from text
   */
  _parseSingleFlightLeg(input, upperInput, data, confidence) {
    // Extract airport codes
    const airports = extractAirportCodes(upperInput);
    if (airports.length >= 2) {
      data.departure_airport = airports[0];
      data.arrival_airport = airports[1];
      confidence.departure_airport = 0.9;
      confidence.arrival_airport = 0.9;
    } else if (airports.length === 1) {
      if (upperInput.includes('FROM ' + airports[0]) || upperInput.includes('DEPARTING ' + airports[0])) {
        data.departure_airport = airports[0];
        confidence.departure_airport = 0.9;
      } else if (upperInput.includes('TO ' + airports[0]) || upperInput.includes('ARRIVING ' + airports[0])) {
        data.arrival_airport = airports[0];
        confidence.arrival_airport = 0.9;
      } else {
        data.departure_airport = airports[0];
        confidence.departure_airport = 0.7;
      }
    }

    // Check for arrow notation: JFK -> LAX or JFK → LAX or JFK - LAX
    const arrowMatch = upperInput.match(/([A-Z]{3})\s*(?:->|→|-|TO)\s*([A-Z]{3})/);
    if (arrowMatch) {
      data.departure_airport = arrowMatch[1];
      data.arrival_airport = arrowMatch[2];
      confidence.departure_airport = 0.95;
      confidence.arrival_airport = 0.95;
    }

    // Extract airline code and flight number
    const flightMatch = input.match(/\b([A-Z]{2})\s*(\d{1,4})\b/i);
    if (flightMatch) {
      data.airline_code = flightMatch[1].toUpperCase();
      data.flight_number = flightMatch[2];
      confidence.airline_code = 0.9;
      confidence.flight_number = 0.9;

      if (AIRLINES[data.airline_code]) {
        data.airline = AIRLINES[data.airline_code].name;
        confidence.airline = 0.9;
      }
    }

    // Extract dates
    const dates = extractDates(input, this.referenceDate);
    if (dates.length >= 1) {
      data.departure_date = dates[0];
      confidence.departure_date = 0.85;
    }
    if (dates.length >= 2) {
      data.arrival_date = dates[1];
      confidence.arrival_date = 0.85;
    }

    // Extract times
    const times = extractTimes(input);
    if (times.length >= 1) {
      data.departure_time = times[0];
      confidence.departure_time = 0.85;
    }
    if (times.length >= 2) {
      data.arrival_time = times[1];
      confidence.arrival_time = 0.85;
    }

    // Infer arrival_date if not explicitly provided
    // If we have departure_date but no arrival_date, and both times exist:
    //   - If arrival_time < departure_time → next day
    //   - Otherwise → same day
    if (data.departure_date && !data.arrival_date && dates.length < 2) {
      if (data.departure_time && data.arrival_time) {
        const depMinutes = this._timeToMinutes(data.departure_time);
        const arrMinutes = this._timeToMinutes(data.arrival_time);
        if (depMinutes !== null && arrMinutes !== null) {
          if (arrMinutes < depMinutes) {
            // Arrival time is earlier → next day
            data.arrival_date = this._addDays(data.departure_date, 1);
            confidence.arrival_date = 0.8;
          } else {
            // Same day
            data.arrival_date = data.departure_date;
            confidence.arrival_date = 0.85;
          }
        }
      } else {
        // No arrival time to compare — assume same day
        data.arrival_date = data.departure_date;
        confidence.arrival_date = 0.7;
      }
    }
  }

  /**
   * Parse hotel-specific fields from natural language
   */
  parseHotelNatural(input, upperInput, data, confidence) {
    // Extract dates (check-in / check-out)
    const dates = extractDates(input, this.referenceDate);
    if (dates.length >= 2) {
      data.check_in_date = dates[0];
      data.check_out_date = dates[1];
      confidence.check_in_date = 0.85;
      confidence.check_out_date = 0.85;
    } else if (dates.length === 1) {
      data.check_in_date = dates[0];
      confidence.check_in_date = 0.7;
    }

    // Extract times
    const times = extractTimes(input);
    if (times.length >= 1) {
      data.check_in_time = times[0];
      confidence.check_in_time = 0.8;
    }
    if (times.length >= 2) {
      data.check_out_time = times[1];
      confidence.check_out_time = 0.8;
    }

    // Extract number of nights
    const nightsMatch = input.match(/(\d+)\s*nights?/i);
    if (nightsMatch) {
      data.number_of_nights = parseInt(nightsMatch[1]);
      confidence.number_of_nights = 0.9;
    }

    // Try to extract hotel name (capitalized words not matching other patterns)
    const hotelMatch = input.match(/(?:hotel|at|stay(?:ing)?)\s+(?:at\s+)?([A-Z][a-zA-Z\s&'-]+?)(?:\s+(?:on|from|check|conf|res)|\d|$)/i);
    if (hotelMatch) {
      data.hotel_name = hotelMatch[1].trim();
      confidence.hotel_name = 0.7;
    }
  }

  /**
   * Parse car rental-specific fields from natural language
   */
  parseCarRentalNatural(input, upperInput, data, confidence) {
    // Extract dates (pickup / dropoff)
    const dates = extractDates(input, this.referenceDate);
    if (dates.length >= 2) {
      data.pickup_date = dates[0];
      data.dropoff_date = dates[1];
      confidence.pickup_date = 0.85;
      confidence.dropoff_date = 0.85;
    } else if (dates.length === 1) {
      data.pickup_date = dates[0];
      confidence.pickup_date = 0.7;
    }

    // Extract times
    const times = extractTimes(input);
    if (times.length >= 1) {
      data.pickup_time = times[0];
      confidence.pickup_time = 0.8;
    }
    if (times.length >= 2) {
      data.dropoff_time = times[1];
      confidence.dropoff_time = 0.8;
    }

    // Extract rental company
    const companies = ['hertz', 'avis', 'enterprise', 'budget', 'national', 'alamo', 'dollar', 'thrifty', 'sixt', 'europcar'];
    for (const company of companies) {
      if (input.toLowerCase().includes(company)) {
        data.rental_company = company.charAt(0).toUpperCase() + company.slice(1);
        confidence.rental_company = 0.9;
        break;
      }
    }

    // Extract car type
    const carTypes = ['economy', 'compact', 'midsize', 'full-size', 'suv', 'luxury', 'convertible', 'minivan', 'pickup'];
    for (const carType of carTypes) {
      if (input.toLowerCase().includes(carType)) {
        data.car_type = carType.charAt(0).toUpperCase() + carType.slice(1);
        confidence.car_type = 0.85;
        break;
      }
    }

    // Extract locations (airport codes often used)
    const airports = extractAirportCodes(upperInput);
    if (airports.length >= 1) {
      data.pickup_location = airports[0];
      confidence.pickup_location = 0.8;
    }
    if (airports.length >= 2) {
      data.dropoff_location = airports[1];
      confidence.dropoff_location = 0.8;
    }
  }

  /**
   * Parse private flight-specific fields (supports multi-leg)
   */
  parsePrivateFlightNatural(input, upperInput, data, confidence) {
    // Try to split into multiple legs
    const legTexts = this._splitIntoLegs(input);

    if (legTexts.length > 1) {
      const legs = [];
      for (let i = 0; i < legTexts.length; i++) {
        const legData = {};
        const legConfidence = {};
        this._parseSinglePrivateFlightLeg(legTexts[i], legTexts[i].toUpperCase(), legData, legConfidence);
        if (Object.keys(legData).length > 0) {
          legData.leg_number = i + 1;
          legs.push(legData);
        }
      }
      if (legs.length > 1) {
        data.legs = legs;
        confidence.legs = 0.85;
        // Extract top-level fields (tail number, aircraft type) from full input
        this._extractPrivateFlightTopLevel(input, upperInput, data, confidence);
        return;
      }
      if (legs.length === 1) {
        Object.assign(data, legs[0]);
        delete data.leg_number;
      }
    }

    // Single leg: parse airports/dates from input
    this._parseSinglePrivateFlightLeg(input, upperInput, data, confidence);
    // Also extract top-level fields
    this._extractPrivateFlightTopLevel(input, upperInput, data, confidence);
  }

  /**
   * Parse a single private flight leg
   */
  _parseSinglePrivateFlightLeg(input, upperInput, data, confidence) {
    // Extract ICAO codes (4 letters)
    const icaoMatches = upperInput.match(/\b[A-Z]{4}\b/g) || [];
    const icaoCodes = icaoMatches.filter(code => /^[A-Z]{4}$/.test(code));
    if (icaoCodes.length >= 2) {
      data.departure_airport_icao = icaoCodes[0];
      data.arrival_airport_icao = icaoCodes[1];
      confidence.departure_airport_icao = 0.85;
      confidence.arrival_airport_icao = 0.85;
    }

    // Also accept IATA codes
    const airports = extractAirportCodes(upperInput);
    if (!data.departure_airport_icao && airports.length >= 1) {
      data.departure_airport_icao = airports[0];
      confidence.departure_airport_icao = 0.7;
    }
    if (!data.arrival_airport_icao && airports.length >= 2) {
      data.arrival_airport_icao = airports[1];
      confidence.arrival_airport_icao = 0.7;
    }

    // Extract dates and times
    const dates = extractDates(input, this.referenceDate);
    if (dates.length >= 1) {
      data.departure_date = dates[0];
      confidence.departure_date = 0.85;
    }

    const times = extractTimes(input);
    if (times.length >= 1) {
      data.departure_time = times[0];
      confidence.departure_time = 0.85;
    }
  }

  /**
   * Extract top-level private flight fields (tail number, aircraft type)
   */
  _extractPrivateFlightTopLevel(input, upperInput, data, confidence) {
    // Extract tail number (N followed by digits/letters)
    if (!data.tail_number) {
      const tailMatch = upperInput.match(/\b(N\d{1,5}[A-Z]{0,2})\b/);
      if (tailMatch) {
        data.tail_number = tailMatch[1];
        confidence.tail_number = 0.9;
      }
    }

    // Extract aircraft type
    if (!data.aircraft_type) {
      const aircraftTypes = ['citation', 'gulfstream', 'challenger', 'global', 'phenom', 'legacy', 'falcon', 'king air', 'learjet', 'hawker', 'pilatus'];
      for (const aircraft of aircraftTypes) {
        if (input.toLowerCase().includes(aircraft)) {
          data.aircraft_type = aircraft.charAt(0).toUpperCase() + aircraft.slice(1);
          confidence.aircraft_type = 0.85;
          break;
        }
      }
    }
  }

  /**
   * Extract common fields that apply to all document types
   */
  extractCommonFields(input, upperInput, data, confidence) {
    // Known company names that should NOT be treated as confirmation numbers
    const KNOWN_COMPANIES = [
      'HERTZ', 'AVIS', 'ENTERPRISE', 'BUDGET', 'NATIONAL', 'ALAMO',
      'DOLLAR', 'THRIFTY', 'SIXT', 'EUROPCAR', 'ZIPCAR', 'TURO',
      'MARRIOTT', 'HILTON', 'HYATT', 'SHERATON', 'WESTIN', 'HOLIDAY',
      'UNITED', 'DELTA', 'AMERICAN', 'SOUTHWEST', 'JETBLUE', 'ALASKA',
      'SPIRIT', 'FRONTIER'
    ];

    // Extract confirmation number (5-8 alphanumeric, not a time or known pattern)
    const confMatches = upperInput.match(/\b([A-Z0-9]{5,8})\b/g) || [];
    for (const match of confMatches) {
      // Skip if it looks like a time, flight number, airport code, or company name
      if (/^\d{4}$/.test(match)) continue; // Year
      if (/^\d{1,2}:\d{2}/.test(match)) continue; // Time
      if (/^[A-Z]{2}\d{1,4}$/.test(match)) continue; // Flight number
      if (extractAirportCodes(match).length > 0) continue; // Airport code
      if (KNOWN_COMPANIES.includes(match)) continue; // Company name

      if (!data.confirmation_number) {
        data.confirmation_number = match;
        confidence.confirmation_number = 0.75;
        break;
      }
    }

    // Look for explicit confirmation patterns
    const explicitConf = input.match(/(?:conf(?:irmation)?|booking|reservation|ref(?:erence)?)\s*(?:#|:)?\s*([A-Z0-9]{4,10})/i);
    if (explicitConf) {
      data.confirmation_number = explicitConf[1].toUpperCase();
      confidence.confirmation_number = 0.95;
    }

    // Extract name - try multiple patterns
    this.extractName(input, upperInput, data, confidence);
  }

  /**
   * Extract passenger/guest/driver name from input
   * Handles formats like:
   * - "John Smith"
   * - "Smith/John" (airline format)
   * - "SMITH/JOHN" (all caps airline format)
   * - "name: John Smith"
   */
  extractName(input, upperInput, data, confidence) {
    // Already have a name from document-type-specific parsing
    if (data.passenger_name || data.guest_name || data.driver_name) {
      return;
    }

    let extractedName = null;
    let nameConfidence = 0.7;

    // Pattern 1: "Last/First" format (common in airline systems)
    const slashNameMatch = input.match(/\b([A-Z][A-Za-z]+)\/([A-Z][A-Za-z]+)\b/);
    if (slashNameMatch) {
      // Convert "Smith/John" to "John Smith"
      extractedName = `${slashNameMatch[2]} ${slashNameMatch[1]}`;
      nameConfidence = 0.9;
    }

    // Pattern 2: ALL CAPS "LAST/FIRST" format
    if (!extractedName) {
      const capsSlashMatch = upperInput.match(/\b([A-Z]{2,})\/([A-Z]{2,})\b/);
      if (capsSlashMatch) {
        // Convert "SMITH/JOHN" to "John Smith" (title case)
        const first = capsSlashMatch[2].charAt(0) + capsSlashMatch[2].slice(1).toLowerCase();
        const last = capsSlashMatch[1].charAt(0) + capsSlashMatch[1].slice(1).toLowerCase();
        extractedName = `${first} ${last}`;
        nameConfidence = 0.9;
      }
    }

    // Pattern 3: Explicit name labels
    if (!extractedName) {
      const labeledPatterns = [
        /(?:passenger|traveler|guest|driver|renter|name|pax)\s*(?::|is|=)?\s*([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/i,
        /(?:for|under|booked by)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)/i
      ];

      for (const pattern of labeledPatterns) {
        const match = input.match(pattern);
        if (match) {
          extractedName = match[1].trim();
          nameConfidence = 0.85;
          break;
        }
      }
    }

    // Pattern 4: Name followed by action verb
    if (!extractedName) {
      const actionMatch = input.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)+)(?:\s+(?:is|flying|traveling|checking|renting|picking up|returning))/i);
      if (actionMatch) {
        extractedName = actionMatch[1].trim();
        nameConfidence = 0.75;
      }
    }

    // Validate and assign name
    if (extractedName) {
      // Basic validation: at least 2 characters per part
      const parts = extractedName.split(/\s+/);
      const isValid = parts.length >= 2 && parts.every(p => p.length >= 2);

      if (isValid) {
        // Assign to appropriate field based on document type
        if (this.documentType === 'hotel') {
          data.guest_name = extractedName;
          confidence.guest_name = nameConfidence;
        } else if (this.documentType === 'car_rental') {
          data.driver_name = extractedName;
          confidence.driver_name = nameConfidence;
        } else {
          data.passenger_name = extractedName;
          confidence.passenger_name = nameConfidence;
        }
      }
    }
  }

  /**
   * Normalize field names to match schema
   */
  normalizeFieldName(input) {
    const normalized = input.toLowerCase().trim().replace(/\s+/g, '_');

    // Common aliases
    const aliases = {
      'from': 'departure_airport',
      'to': 'arrival_airport',
      'depart': 'departure_airport',
      'arrive': 'arrival_airport',
      'departure': 'departure_airport',
      'arrival': 'arrival_airport',
      'dep': 'departure_airport',
      'arr': 'arrival_airport',
      'date': 'departure_date',
      'time': 'departure_time',
      'flight': 'flight_number',
      'flt': 'flight_number',
      'conf': 'confirmation_number',
      'confirmation': 'confirmation_number',
      'booking': 'confirmation_number',
      'reservation': 'confirmation_number',
      'ref': 'confirmation_number',
      'pax': 'passenger_name',
      'passenger': 'passenger_name',
      'traveler': 'passenger_name',
      'guest': 'guest_name',
      'driver': 'driver_name',
      'company': 'rental_company',
      'vendor': 'rental_company',
      'hotel': 'hotel_name',
      'property': 'hotel_name',
      'checkin': 'check_in_date',
      'check_in': 'check_in_date',
      'checkout': 'check_out_date',
      'check_out': 'check_out_date',
      'pickup': 'pickup_date',
      'dropoff': 'dropoff_date',
      'return': 'dropoff_date'
    };

    return aliases[normalized] || normalized;
  }

  /**
   * Normalize and validate extracted data
   */
  /**
   * Convert HH:MM time string to minutes since midnight
   */
  _timeToMinutes(timeStr) {
    const match = timeStr.match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;
    return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
  }

  /**
   * Add days to a YYYY-MM-DD date string
   */
  _addDays(dateStr, days) {
    const d = new Date(dateStr + 'T00:00:00');
    d.setDate(d.getDate() + days);
    return d.toISOString().split('T')[0];
  }

  normalizeData(data) {
    const normalized = { ...data };

    // Normalize airport codes to uppercase
    const airportFields = ['departure_airport', 'arrival_airport', 'departure_airport_icao', 'arrival_airport_icao'];
    for (const field of airportFields) {
      if (normalized[field]) {
        normalized[field] = normalized[field].toUpperCase();
      }
    }

    // Normalize airline code
    if (normalized.airline_code) {
      normalized.airline_code = normalized.airline_code.toUpperCase();
    }

    // Normalize confirmation number
    if (normalized.confirmation_number) {
      normalized.confirmation_number = normalized.confirmation_number.toUpperCase();
    }

    // Ensure dates are in ISO format
    const dateFields = ['departure_date', 'arrival_date', 'check_in_date', 'check_out_date', 'pickup_date', 'dropoff_date'];
    for (const field of dateFields) {
      if (normalized[field] && !/^\d{4}-\d{2}-\d{2}/.test(normalized[field])) {
        const parsed = parseDate(normalized[field], this.referenceDate);
        if (parsed) {
          normalized[field] = parsed;
        }
      }
    }

    // Ensure times are in 24-hour format
    const timeFields = ['departure_time', 'arrival_time', 'check_in_time', 'check_out_time', 'pickup_time', 'dropoff_time'];
    for (const field of timeFields) {
      if (normalized[field] && !/^\d{2}:\d{2}/.test(normalized[field])) {
        const parsed = parseTime(normalized[field]);
        if (parsed) {
          normalized[field] = parsed;
        }
      }
    }

    // Normalize legs array if present
    if (Array.isArray(normalized.legs)) {
      normalized.legs = normalized.legs.map(leg => this.normalizeData(leg));
    }

    return normalized;
  }

  /**
   * Calculate confidence scores for extracted fields
   */
  calculateConfidence(data) {
    const confidence = {};

    for (const [field, value] of Object.entries(data)) {
      if (value === undefined || value === null || value === '') {
        continue;
      }

      // Default confidence based on field type
      if (field.includes('airport') && isValidAirportCode(value)) {
        confidence[field] = 0.9;
      } else if (field === 'confirmation_number' && /^[A-Z0-9]{5,8}$/.test(value)) {
        confidence[field] = 0.85;
      } else if (field.includes('date') && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        confidence[field] = 0.85;
      } else if (field.includes('time') && /^\d{2}:\d{2}/.test(value)) {
        confidence[field] = 0.85;
      } else {
        confidence[field] = 0.7;
      }
    }

    return confidence;
  }
}

/**
 * Create a parser for a specific document type
 */
export const createParser = (documentType, referenceDate) => {
  return new SmartInputParser(documentType, referenceDate);
};

/**
 * Quick parse function
 */
export const parseSmartInput = (input, documentType = 'flight', referenceDate = new Date()) => {
  const parser = new SmartInputParser(documentType, referenceDate);
  return parser.parse(input);
};

export default SmartInputParser;
